import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Portfolio = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);

  const portfolioItems = [
    {
      id: 1,
      title: 'MP Assembly Elections 2023',
      subtitle: 'Madhya Pradesh State Campaign',
      category: 'state',
      duration: '6 months',
      description: 'Comprehensive campaign strategy covering 230 constituencies with targeted social media outreach and booth-level organization.',
      previousVote: 32,
      achievedVote: 48,
      margin: '+16% increase',
      gradient: 'from-jetBlue-500 to-red-500',
      details: {
        overview: 'A comprehensive digital-first campaign strategy for Madhya Pradesh Assembly Elections 2023, covering 230 constituencies with targeted messaging and grassroots mobilization.',
        strategies: [
          'Constituency-wise voter analysis and micro-targeting',
          'Multi-language social media campaigns',
          'Booth-level volunteer training programs',
          'Real-time sentiment monitoring and response',
          'Traditional media integration with digital campaigns'
        ],
        results: {
          seatsWon: '163/230',
          socialReach: '50M+ impressions',
          volunteers: '25,000+ trained'
        }
      }
    },
    {
      id: 2,
      title: 'Lok Sabha Campaign 2024',
      subtitle: 'Parliamentary Constituency',
      category: 'lok-sabha',
      duration: '8 months',
      description: 'Multi-constituency approach with coordinated messaging, influencer partnerships, and grassroots mobilization.',
      previousVote: 45,
      achievedVote: 62,
      margin: '1.2L votes',
      gradient: 'from-orange-500 to-purple-500',
      details: {
        overview: 'National-level parliamentary campaign with strategic coordination across multiple constituencies and comprehensive digital outreach.',
        strategies: [
          'Multi-constituency coordination strategy',
          'Influencer and celebrity endorsements',
          'Grassroots volunteer mobilization',
          'Data-driven voter targeting',
          'Cross-platform digital campaigns'
        ],
        results: {
          seatsWon: '12/15',
          socialReach: '100M+ impressions',
          volunteers: '50,000+ trained'
        }
      }
    },
    {
      id: 3,
      title: 'Municipal Elections 2023',
      subtitle: 'City Corporation Campaign',
      category: 'municipal',
      duration: '4 months',
      description: 'Ward-wise strategy with community engagement, local issue focus, and door-to-door campaigning coordination.',
      previousVote: 28,
      achievedVote: 54,
      margin: '42/65 wards',
      gradient: 'from-green-500 to-blue-500',
      details: {
        overview: 'Local governance campaign focused on ward-level strategies and community-specific issues with direct voter engagement.',
        strategies: [
          'Ward-wise micro-targeting',
          'Community leader engagement',
          'Local issue-based messaging',
          'Door-to-door campaign coordination',
          'Hyperlocal social media strategy'
        ],
        results: {
          wardsWon: '42/65',
          socialReach: '5M+ local impressions',
          volunteers: '5,000+ trained'
        }
      }
    }
  ];

  const filters = [
    { id: 'all', label: 'All Campaigns' },
    { id: 'state', label: 'State Elections' },
    { id: 'lok-sabha', label: 'Lok Sabha' },
    { id: 'municipal', label: 'Municipal' }
  ];

  const filteredItems = activeFilter === 'all'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === activeFilter);

  return (
    <section id="portfolio" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-20">
          <motion.div
            className="inline-flex items-center space-x-3 bg-jetBlue-100 text-jetBlue-800 rounded-full px-6 py-3 mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <span className="text-xl">📊</span>
            <span className="font-semibold tracking-wide">CAMPAIGN PORTFOLIO</span>
          </motion.div>

          <motion.h2
            className="display-text text-gray-900 mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            Successful Political
            <span className="text-gradient-political block mt-2">Campaign Victories</span>
          </motion.h2>

          <motion.p
            className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            Explore our detailed case studies showcasing strategic excellence, measurable results,
            and electoral victories across different political landscapes in India.
          </motion.p>
        </div>

        {/* Portfolio Filters */}
        <motion.div
          className="flex flex-wrap justify-center gap-4 mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.2, delay: 0.1 }}
        >
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeFilter === filter.id
                  ? 'bg-jetBlue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-jetBlue-100 hover:text-jetBlue-600'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </motion.div>

        {/* Portfolio Grid */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                className="bg-white rounded-3xl shadow-xl overflow-hidden group cursor-pointer"
                initial={{ opacity: 0, y: 80, scale: 0.95 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedCase(item)}
              >
                <div className={`relative h-48 bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                  <div className="text-white text-center">
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-sm opacity-90">{item.subtitle}</p>
                  </div>
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    VICTORY
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                      item.category === 'state' ? 'text-jetBlue-600 bg-jetBlue-50' :
                      item.category === 'lok-sabha' ? 'text-orange-600 bg-orange-50' :
                      'text-green-600 bg-green-50'
                    }`}>
                      {item.category === 'state' ? 'State Election' :
                       item.category === 'lok-sabha' ? 'Lok Sabha' : 'Municipal'}
                    </span>
                    <span className="text-sm text-gray-500">{item.duration}</span>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Strategic Digital Campaign</h4>
                  <p className="text-gray-600 text-sm mb-4">{item.description}</p>

                  {/* Before/After Metrics */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <div className="text-lg font-bold text-red-600">{item.previousVote}%</div>
                      <div className="text-xs text-gray-600">Previous Vote Share</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-lg font-bold text-green-600">{item.achievedVote}%</div>
                      <div className="text-xs text-gray-600">Achieved Vote Share</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      <span className="font-medium">Margin:</span> {item.margin}
                    </div>
                    <div className="text-jetBlue-600 font-medium text-sm group-hover:translate-x-2 transition-transform duration-300">
                      View Details →
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Modal for Case Study Details */}
        <AnimatePresence>
          {selectedCase && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedCase(null)}
            >
              <motion.div
                className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedCase.title} - Detailed Case Study</h2>
                  <button
                    onClick={() => setSelectedCase(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Campaign Overview</h3>
                    <p className="text-gray-600 mb-6">{selectedCase.details.overview}</p>

                    <h4 className="font-semibold mb-3">Key Strategies Implemented:</h4>
                    <ul className="space-y-2 text-gray-600 mb-6">
                      {selectedCase.details.strategies.map((strategy, index) => (
                        <li key={index}>• {strategy}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4">Results & Impact</h3>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600 mb-1">{selectedCase.previousVote}%</div>
                        <div className="text-sm text-gray-600">Previous Vote Share</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 mb-1">{selectedCase.achievedVote}%</div>
                        <div className="text-sm text-gray-600">Achieved Vote Share</div>
                      </div>
                    </div>

                    <div className="space-y-4 mb-6">
                      {Object.entries(selectedCase.details.results).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                          <span className="font-semibold">{value}</span>
                        </div>
                      ))}
                    </div>

                    <h4 className="font-semibold mb-3">Client Testimonial:</h4>
                    <blockquote className="bg-gray-50 p-4 rounded-lg italic text-gray-700">
                      "Jet Digital Infotech's strategic approach and digital expertise were instrumental in our
                      historic victory. Their data-driven methodology and ground-level execution made all the difference."
                      <footer className="text-sm text-gray-500 mt-2">- Campaign Manager, {selectedCase.title}</footer>
                    </blockquote>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Portfolio;
